/**
 * ErrorState — single source of "this panel's query failed" UX.
 * Renders a sanitized error message + a Retry button so the user
 * can recover from transient failures without reloading the page.
 *
 * The component is intentionally minimal: it does not catch
 * errors itself (that's `ErrorBoundary`'s job for render-time
 * throws) and it does not own the retry logic (that lives on the
 * `QueryState<T>.error` discriminant from `useQueryWithState`).
 * It is purely a presentation primitive that takes an `Error`
 * value and an `onRetry` callback.
 *
 * Production-mode message gating:
 *   Backend errors can carry internal identifiers, table names,
 *   stack fragments, or operational details (e.g. Convex auth
 *   tokens, function names, dev-only debug strings). Rendering
 *   `error.message` raw to the DOM in production would leak
 *   these to subscribers. We mirror the gating pattern from
 *   `ErrorBoundary.tsx` (QST-214 review pass 1, Qodo finding):
 *     * Production builds (`import.meta.env.PROD`) get a generic
 *       "Couldn't load this panel" message.
 *     * Dev / preview builds keep the raw `error.message` so
 *       engineers debugging locally see the real failure.
 *
 * Console logging:
 *   Browser `console.error` is visible to subscribers in prod, so
 *   we DO NOT log raw errors there once we ship to subscribers
 *   (QST-214 review pass 2, Qodo finding 'Information exposure').
 *   Behaviour:
 *     * Dev builds: log raw error to `console.error` so engineers
 *       see the real failure during development.
 *     * Prod builds: silent in console; raw error is forwarded to
 *       a registrable telemetry sink (see `setErrorStateTelemetry`)
 *       so a future Sentry/Datadog/etc. integration can pick it
 *       up server-side without leaking via the browser console.
 *
 *   Dedup: each (label, error.message) pair is logged at most
 *   once per page lifetime. React panels can re-render multiple
 *   times for the same failed state (parent prop change, retry
 *   counter bump, etc.) and we don't want to spam either dev
 *   tooling or telemetry with duplicates.
 */
import { useEffect } from "react";
interface ErrorStateProps {
  error: Error;
  onRetry: () => void;
  /**
   * Optional contextual label, e.g. "Stats" or "Signals". Shown
   * before "failed to load" so the user knows which panel
   * misbehaved when several sit on the same page.
   */
  label?: string;
  /**
   * Mirror of {@link LoadingState} — full-width when the panel
   * owns its row, inline otherwise.
   */
  block?: boolean;
}

/**
 * Telemetry sink + dedupe set are deliberately module-scoped
 * globals (QST-214 review pass 3, Qodo finding 'Global State'):
 *
 *   * The dashboard is a single-React-root single-page app with
 *     no SSR; there is exactly one module instance per page load,
 *     so module-scoped state IS session-scoped state in practice.
 *   * Cross-panel dedupe is the desired behaviour: if Convex is
 *     down, every panel surfaces the same connection error, and
 *     the operator only wants to see it once — not four times.
 *   * Telemetry registration must be a singleton; multiple sinks
 *     would either silently lose the second one or fan out to
 *     both, both of which violate the 'install once at
 *     bootstrap' contract that Sentry/Datadog expect.
 *
 * If the dashboard ever gains a second React root, an SSR pass,
 * or a multi-tenant context, move both pieces of state behind a
 * React context and replace `setErrorStateTelemetry` with a
 * `<TelemetryProvider>` wrapper. Tracked as a follow-up; not
 * needed for the current single-root single-tenant deployment.
 */
type TelemetrySink = (label: string | undefined, error: Error) => void;
let telemetrySink: TelemetrySink = () => {};

export function setErrorStateTelemetry(sink: TelemetrySink): void {
  telemetrySink = sink;
}

/** Module-private dedupe set, keyed by `${label}::${message}`. */
const seenErrors = new Set<string>();

/** Test-only: clear the dedupe set so tests don't bleed across cases. */
export function _resetErrorStateDedupe(): void {
  seenErrors.clear();
}

export function ErrorState({
  error,
  onRetry,
  label,
  block = false,
}: ErrorStateProps) {
  const heading = label ? `${label} failed to load` : "Failed to load";
  const isProd = import.meta.env.PROD;

  // Side effects (logging + telemetry) live in useEffect so they
  // fire once per (label, error.message) per error episode. The
  // dedupe key is dropped when ErrorState unmounts (panel
  // transitions back to LoadingState / successful render), so
  // the next genuine re-failure for the same panel + message
  // logs again. This matches operator expectations:
  //   * Same error spamming on every Retry click → logged once.
  //   * Genuine recovery, then a fresh re-failure later →
  //     logged again so the operator sees the regression.
  // (QST-214 review pass 4, Qodo finding 'Global Dedupe'.)
  useEffect(() => {
    const dedupeKey = `${label ?? ""}::${error.message}`;
    if (seenErrors.has(dedupeKey)) {
      // Already logged this episode — do nothing on this render,
      // but DO leave the dedupe entry in place. Cleanup runs
      // when the ErrorState unmounts (i.e. the panel recovered
      // or the error message changed) which is when we want to
      // forget this entry.
      return () => {
        seenErrors.delete(dedupeKey);
      };
    }
    seenErrors.add(dedupeKey);

    if (!isProd && typeof console !== "undefined") {
      // Dev only: full error to dev console so engineers see
      // the real failure during local development. Browser
      // console output is visible to anyone with the page open
      // in prod, so we deliberately stay silent there.
      console.error(`[ErrorState${label ? `:${label}` : ""}]`, error);
    }

    // Telemetry sink ALWAYS receives the raw error (dev + prod).
    // Default sink is a no-op until application bootstrap
    // installs a real one (Sentry, Datadog, etc.).
    try {
      telemetrySink(label, error);
    } catch {
      // Swallow sink failures — a bad telemetry handler must
      // never break the UI.
    }

    return () => {
      // Drop the dedupe entry when this ErrorState instance
      // unmounts. Next time the panel hits the same error
      // message we will log + telemetry again — which is the
      // correct semantics for 'genuine re-failure after
      // recovery'.
      seenErrors.delete(dedupeKey);
    };
  }, [label, error, isProd]);

  const message = isProd
    ? "Couldn't load this panel. Try Retry, or reload the page if the problem persists."
    : error.message;

  return (
    <div
      className={`state-block state-block--error${block ? " state-block--block" : ""}`}
      role="alert"
    >
      <div className="state-block-title">{heading}</div>
      <div className="state-block-message">{message}</div>
      <button
        type="button"
        className="state-block-button"
        onClick={onRetry}
      >
        Retry
      </button>
    </div>
  );
}
